import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type ReportsRow = Database['public']['Tables']['reports']['Row'];
type ReportsInsert = Database['public']['Tables']['reports']['Insert'];
type ReportsUpdate = Database['public']['Tables']['reports']['Update'];

export interface Report extends ReportsRow {}

export interface ReportFile {
  id: string;
  report_id: string;
  name: string;
  file_path: string;
  mime_type?: string;
  file_size?: number;
  uploaded_by?: string;
  created_at: string;
}

export const reportsService = {
  async getReports() {
    const { data, error } = await supabase
      .from('reports')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as Report[];
  },

  async getReportById(id: string) {
    const { data, error } = await supabase
      .from('reports')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as Report;
  },

  async createReport(report: ReportsInsert) {
    const { data, error } = await supabase
      .from('reports')
      .insert([report])
      .select()
      .single();

    if (error) throw error;
    return data as Report;
  },

  async updateReport(id: string, updates: ReportsUpdate) {
    const { data, error } = await supabase
      .from('reports')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Report;
  },

  async deleteReport(id: string) {
    const { error } = await supabase
      .from('reports')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async getReportsByCategory(category: string) {
    const { data, error } = await supabase
      .from('reports')
      .select('*')
      .eq('category', category)
      .order('season', { ascending: false });

    if (error) throw error;
    return data as Report[];
  },

  async getReportFiles(reportId: string) {
    const { data, error } = await supabase
      .from('report_files')
      .select('*')
      .eq('report_id', reportId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as ReportFile[];
  },

  async uploadReportFile(reportId: string, file: File) {
    const fileExt = file.name.split('.').pop();
    const fileName = `${crypto.randomUUID()}.${fileExt}`;
    const filePath = `${reportId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('report-files')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data, error } = await supabase
      .from('report_files')
      .insert([{
        report_id: reportId,
        name: file.name,
        file_path: filePath,
        mime_type: file.type,
        file_size: file.size,
      }])
      .select()
      .single();

    if (error) throw error;
    return data as ReportFile;
  },

  async deleteReportFile(fileId: string, filePath: string) {
    const { error: storageError } = await supabase.storage
      .from('report-files')
      .remove([filePath]);

    if (storageError) throw storageError;

    const { error } = await supabase
      .from('report_files')
      .delete()
      .eq('id', fileId);

    if (error) throw error;
  },

  async getAnalyticsData() {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    // Get completed reports in last 30 days
    const { data: completedReports, error: completedError } = await supabase
      .from('reports')
      .select('*')
      .eq('status', 'Uploaded')
      .gte('delivery_date', thirtyDaysAgo.toISOString().split('T')[0]);

    if (completedError) throw completedError;

    // Get in progress reports
    const { data: inProgressReports, error: inProgressError } = await supabase
      .from('reports')
      .select('*')
      .eq('status', 'In Progress');

    if (inProgressError) throw inProgressError;

    // Get planned reports for next 30 days
    const { data: plannedReports, error: plannedError } = await supabase
      .from('reports')
      .select('*')
      .eq('status', 'Planned')
      .lte('start_date', thirtyDaysFromNow.toISOString().split('T')[0]);

    if (plannedError) throw plannedError;

    return {
      completed: completedReports || [],
      inProgress: inProgressReports || [],
      planned: plannedReports || []
    };
  }
};